import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dataService from 'services/dataService';
import { API_PODS } from 'services/dataService/resources';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';

import { ApiError } from '../utils/api-error';
import { classifyPodError } from '../utils/pod-error-classifier';

import type { CreatePodResponse } from '../types';
import type { ICreatePodFormValues } from '../components/create-pod-modal/validation-schema';

interface IUpdatePodPayload {
  podName?: string;
  status?: 'active' | 'inactive';
  states?: string[];
  clinicGuids?: string[];
  members?: Array<{ loginGuid: string; priority: string }>;
}

interface IUseUpdatePodOptions {
  onSuccess?: () => void;
  onDuplicateName?: (message: string) => void;
  onInactivePod?: (message: string) => void;
}

const computeChangedFields = (
  original: ICreatePodFormValues,
  current: ICreatePodFormValues,
): IUpdatePodPayload => {
  const payload: IUpdatePodPayload = {};

  if (original.podName !== current.podName) {
    payload.podName = current.podName;
  }

  if (original.isActive !== current.isActive) {
    payload.status = current.isActive ? 'active' : 'inactive';
  }

  const statesChanged =
    original.states.length !== current.states.length ||
    original.states.some((s) => !current.states.includes(s)) ||
    current.states.some((s) => !original.states.includes(s));
  if (statesChanged) {
    payload.states = current.states;
  }

  const originalClinicGuids = original.clinicGuids.map((c) => c.guid).sort();
  const currentClinicGuids = current.clinicGuids.map((c) => c.guid).sort();
  const clinicsChanged =
    originalClinicGuids.length !== currentClinicGuids.length ||
    originalClinicGuids.some((g, i) => g !== currentClinicGuids[i]);
  if (clinicsChanged) {
    payload.clinicGuids = current.clinicGuids.map((c) => c.guid);
  }

  const originalMemberKeys = original.members
    .map((m) => `${m.loginGuid}:${m.priority}`)
    .sort();
  const currentMemberKeys = current.members
    .map((m) => `${m.loginGuid}:${m.priority}`)
    .sort();
  const membersChanged =
    originalMemberKeys.length !== currentMemberKeys.length ||
    originalMemberKeys.some((k, i) => k !== currentMemberKeys[i]);
  if (membersChanged) {
    payload.members = current.members.map((m) => ({
      loginGuid: m.loginGuid,
      priority: m.priority,
    }));
  }

  return payload;
};

export const useUpdatePod = ({
  onSuccess,
  onDuplicateName,
  onInactivePod,
}: IUseUpdatePodOptions = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: async ({
      guid,
      payload,
    }: {
      guid: string;
      payload: IUpdatePodPayload;
    }) => {
      const { data, error, status } =
        await dataService.patchOne<CreatePodResponse>(API_PODS, guid, payload);
      if (error) {
        throw new ApiError(error, status);
      }
      return data;
    },
    onSuccess: (data) => {
      notificationController.success({
        message: t('labels.podUpdatedSuccessfully'),
      });

      if (data?.advisories?.length) {
        const primaryPattern = /primary.*assignment.*another.*active.*pod/i;
        const primaryAdvisories: typeof data.advisories = [];
        const otherAdvisories: typeof data.advisories = [];

        data.advisories.forEach((a) => {
          (primaryPattern.test(a.message)
            ? primaryAdvisories
            : otherAdvisories
          ).push(a);
        });

        if (primaryAdvisories.length > 1) {
          notificationController.info({
            message: t('labels.memberPrimaryAssignmentAdvisory'),
          });
        } else if (primaryAdvisories.length === 1) {
          notificationController.info({
            message: primaryAdvisories[0].message,
          });
        }

        otherAdvisories.forEach((advisory) => {
          notificationController.info({ message: advisory.message });
        });
      }

      queryClient.invalidateQueries({ queryKey: [API_PODS, 'list'] });
      queryClient.invalidateQueries({ queryKey: [API_PODS, 'detail'] });
      queryClient.invalidateQueries({ queryKey: ['pod-assignments', 'RPM'] });

      onSuccess?.();
    },
    onError: (error: unknown) => {
      const classified = classifyPodError(error);

      if (classified.type === 'DUPLICATE_POD_NAME') {
        const msg = classified.messageKey
          ? t(classified.messageKey, classified.fallback)
          : classified.fallback;
        onDuplicateName?.(msg);
        return;
      }

      if (classified.type === 'INACTIVE_POD') {
        const msg = classified.messageKey
          ? t(classified.messageKey, classified.fallback)
          : classified.fallback;
        onInactivePod?.(msg);
        return;
      }

      if (classified.type === 'INSUFFICIENT_PRIVILEGES') {
        notificationController.error({
          message: classified.messageKey
            ? t(classified.messageKey, classified.fallback)
            : classified.fallback,
        });
        return;
      }

      if (classified.type === 'POD_NOT_FOUND') {
        notificationController.error({
          message: classified.messageKey
            ? t(classified.messageKey, classified.fallback)
            : classified.fallback,
        });
        queryClient.invalidateQueries({ queryKey: [API_PODS, 'list'] });
        return;
      }

      // VALIDATION_ERROR (400) and GENERIC (500+/unknown):
      notificationController.error({
        message: classified.messageKey
          ? t(classified.messageKey, classified.fallback)
          : classified.fallback,
      });
    },
  });

  const updatePod = (
    guid: string,
    original: ICreatePodFormValues,
    current: ICreatePodFormValues,
  ) => {
    const payload = computeChangedFields(original, current);

    if (Object.keys(payload).length === 0) {
      onSuccess?.();
      return;
    }

    mutate({ guid, payload });
  };

  return { updatePod, computeChangedFields, isLoading };
};
